import { Icon, ICONS } from 'components/Icon';
import { Page } from 'components/Page';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { UI_DATE_FORMAT_LONG } from 'lib/constants';
import { pluralise } from 'lib/pluralise';
import { formatDate } from 'lib/riskbase';
import { theme } from 'lib/theme';
import * as React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  doAsesListLoad,
  doAsesAssessmentLoad,
  doAsesListFilterLoad,
  doAsesListFilterOpenModal,
} from 'store/ases/actions';
import {
  selectAsesListDataAssessmentStatuses,
  selectAsesListEverLoaded,
  selectAsesListIds,
  selectAsesListItem,
  selectAsesListTotalCount,
} from 'store/ases/selectors';
import { AssessmentStatus } from 'types/types';
import { PageEmpty } from 'components/PageEmpty';
import { Button, ButtonTheme } from 'components/Button';
import { AssessmentListHeader } from './AssessmentListHeader';
import { Grid, GridProps } from 'components/Grid';
import { ReferenceToText } from 'components/ReferenceText';
import { AssessmentListLoader } from './AssessmentListLoader';
import { ButtonGroup } from 'components/ButtonGroup';

type DefinedGridProps = GridProps<
  'assessment' | 'property' | 'assessed' | 'approved',
  string,
  {
    assessmentStatuses: Record<string, AssessmentStatus>;
    onRowPress: (id: string) => void;
  }
>;

const useGetRowData: DefinedGridProps['getRowData'] = ({ id, extraData }) => {
  const data = useSelector((state) => selectAsesListItem(state, id));
  return React.useMemo(() => {
    if (!data) {
      return null;
    }
    return {
      onPress: () => extraData.onRowPress(data.id),
      columns: {
        assessment: () => {
          const status = extraData.assessmentStatuses[data.status];
          return (
            <View
              style={{
                flexDirection: 'row',
                flex: 1,
                alignItems: 'center',
                columnGap: theme.padding,
              }}
            >
              <Icon
                source={ICONS.FileDuotone}
                color={status ? `#${status.color}` : undefined}
              />
              <View style={{ flex: 1 }}>
                <Txt numberOfLines={1} weight="bold">
                  {data.type.name}
                </Txt>
                <ReferenceToText
                  reference={data.reference}
                  customReference={data.customReference}
                />
              </View>
            </View>
          );
        },
        property: () => {
          return (
            <View
              style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}
            >
              <View style={{ flex: 1 }}>
                <Txt numberOfLines={1} weight="bold">
                  {data.property.name}
                </Txt>
                <ReferenceToText
                  reference={data.property.reference}
                  customReference={data.property.customReference}
                  organisation={data.forOrganisation.name}
                />
              </View>
            </View>
          );
        },
        assessed: () => {
          return (
            <View
              style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}
            >
              <View style={{ flex: 1 }}>
                <Txt numberOfLines={1}>
                  {formatDate(data.assessed.date, UI_DATE_FORMAT_LONG)}
                </Txt>
                <Txt numberOfLines={1} size="xsmall">
                  {data.assessed.by.map((by) => by.name).join(', ')}
                </Txt>
              </View>
            </View>
          );
        },
        approved: () => {
          return (
            <View
              style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}
            >
              {data.approved ? (
                <View style={{ flex: 1 }}>
                  <Txt numberOfLines={1}>
                    {formatDate(data.approved.date, UI_DATE_FORMAT_LONG)}
                  </Txt>
                  <Txt numberOfLines={1} size="xsmall">
                    {data.approved.by.map((by) => by.name).join(', ')}
                  </Txt>
                </View>
              ) : (
                <Txt>-</Txt>
              )}
            </View>
          );
        },
      },
    };
  }, [data, extraData]);
};

export const AssessmentListScreen: React.FC = () => {
  const dispatch = useDispatch();

  const listTotalCount = useSelector(selectAsesListTotalCount);
  const listEverLoaded = useSelector(selectAsesListEverLoaded);
  const gridIds = useSelector(selectAsesListIds);
  const remainingCount = listTotalCount - gridIds.length;
  const assessmentStatuses = useSelector(selectAsesListDataAssessmentStatuses);

  React.useEffect(() => {
    dispatch(
      doAsesListFilterLoad({
        setDraft: false,
        organisationIds: null,
      }),
    );
  }, [dispatch]);

  const handleRowPress = React.useCallback(
    (id: string) =>
      dispatch(
        doAsesAssessmentLoad({
          id,
        }),
      ),
    [dispatch],
  );

  const [columns] = React.useState<DefinedGridProps['columns']>([
    {
      name: 'Assessment',
      dataIndex: 'assessment',
      minWidth: 150,
      flex: 1,
    },
    {
      name: 'Property',
      dataIndex: 'property',
      minWidth: 150,
      flex: 2,
    },
    {
      name: 'Assessed',
      dataIndex: 'assessed',
      minWidth: 125,
      maxWidth: 180,
    },
    {
      name: 'Approved',
      dataIndex: 'approved',
      minWidth: 125,
      maxWidth: 180,
    },
  ]);

  return (
    <>
      <AssessmentListLoader />
      <Page header={<AssessmentListHeader />}>
        {!listEverLoaded ? (
          <PageEmpty icon={ICONS.RBPlectrumLight}>
            <ButtonGroup>
              <Button
                text="Load"
                icon={ICONS.RotateRightLight}
                buttonTheme={ButtonTheme.themeless}
                onPress={() =>
                  dispatch(
                    doAsesListLoad({
                      append: false,
                      useDraftFilter: false,
                      useBlockingLoader: false,
                    }),
                  )
                }
              />
              <Button
                text="Filter"
                icon={ICONS.SlidersUpLight}
                buttonTheme={ButtonTheme.themeless}
                onPress={() =>
                  dispatch(
                    doAsesListFilterOpenModal({
                      setDraft: false,
                    }),
                  )
                }
              />
            </ButtonGroup>
          </PageEmpty>
        ) : !gridIds.length ? (
          <PageEmpty
            text="No Assessments Found"
            subtext="Try changing your filter"
            icon={ICONS.ListLight}
            iconCircled={false}
            style={{ backgroundColor: theme.colors.background }}
          />
        ) : (
          <Grid
            columns={columns}
            data={gridIds}
            extraData={{
              assessmentStatuses,
              onRowPress: handleRowPress,
            }}
            getRowData={useGetRowData}
            keyExtractor={(i) => `${i}`}
            ListFooterComponent={
              remainingCount > 0 ? (
                <View
                  style={{
                    padding: theme.padding * 2,
                    paddingTop: theme.padding,
                    alignItems: 'flex-start',
                  }}
                >
                  <Button
                    text={pluralise(
                      remainingCount,
                      '{x} More Result, Load',
                      '{x} More Results, Load More',
                    )}
                    onPress={() =>
                      dispatch(
                        doAsesListLoad({
                          append: true,
                          useBlockingLoader: false,
                        }),
                      )
                    }
                    icon={ICONS.ArrowDownLight}
                  />
                </View>
              ) : (
                <View style={{ height: theme.padding * 2 }} />
              )
            }
          ></Grid>
        )}
      </Page>
    </>
  );
};
